import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export type CompanyFollowState = {
  isFollowing: boolean;
  followersCount: number;
  /** True once seeded from a post/page with a real server count */
  hydrated: boolean;
};

type SeedInput = {
  isFollowing: boolean;
  followersCount: number;
};

type CompanyFollowContextValue = {
  getState: (companyId: string) => CompanyFollowState | undefined;
  /** Seed from GraphQL when a post/page first loads. Does not overwrite hydrated state. */
  seedCompany: (companyId: string, state: SeedInput) => void;
  /** Update follow flag and ±1 followers count (optimistic / after mutation). */
  applyFollowChange: (companyId: string, nextIsFollowing: boolean) => void;
  /** Adjust followers count only (e.g. owner removes another follower). */
  adjustFollowersCount: (companyId: string, delta: number) => void;
  /** Set absolute followers count after a known server value. */
  setFollowersCount: (companyId: string, followersCount: number) => void;
};

const CompanyFollowContext = createContext<CompanyFollowContextValue | null>(
  null,
);

export const CompanyFollowProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [byCompanyId, setByCompanyId] = useState<
    Record<string, CompanyFollowState>
  >({});

  const getState = useCallback(
    (companyId: string) => byCompanyId[companyId],
    [byCompanyId],
  );

  const seedCompany = useCallback((companyId: string, incoming: SeedInput) => {
    if (!companyId) return;

    setByCompanyId((prev) => {
      const cur = prev[companyId];

      if (!cur) {
        return {
          ...prev,
          [companyId]: {
            isFollowing: incoming.isFollowing,
            followersCount: Math.max(0, incoming.followersCount),
            hydrated: true,
          },
        };
      }

      // Stub from a follow elsewhere (e.g. suggested list) — hydrate count from server
      if (!cur.hydrated) {
        const followersCount =
          cur.isFollowing === incoming.isFollowing
            ? incoming.followersCount
            : Math.max(
                0,
                incoming.followersCount + (cur.isFollowing ? 1 : -1),
              );

        return {
          ...prev,
          [companyId]: {
            isFollowing: cur.isFollowing,
            followersCount,
            hydrated: true,
          },
        };
      }

      return prev;
    });
  }, []);

  const applyFollowChange = useCallback(
    (companyId: string, nextIsFollowing: boolean) => {
      if (!companyId) return;

      setByCompanyId((prev) => {
        const cur = prev[companyId];

        if (!cur) {
          return {
            ...prev,
            [companyId]: {
              isFollowing: nextIsFollowing,
              followersCount: 0,
              hydrated: false,
            },
          };
        }

        if (cur.isFollowing === nextIsFollowing) return prev;

        return {
          ...prev,
          [companyId]: {
            ...cur,
            isFollowing: nextIsFollowing,
            followersCount: Math.max(
              0,
              cur.followersCount + (nextIsFollowing ? 1 : -1),
            ),
          },
        };
      });
    },
    [],
  );

  const adjustFollowersCount = useCallback(
    (companyId: string, delta: number) => {
      if (!companyId || delta === 0) return;

      setByCompanyId((prev) => {
        const cur = prev[companyId];
        if (!cur) {
          return {
            ...prev,
            [companyId]: {
              isFollowing: false,
              followersCount: Math.max(0, delta),
              hydrated: false,
            },
          };
        }

        return {
          ...prev,
          [companyId]: {
            ...cur,
            followersCount: Math.max(0, cur.followersCount + delta),
          },
        };
      });
    },
    [],
  );

  const setFollowersCount = useCallback(
    (companyId: string, followersCount: number) => {
      if (!companyId) return;

      setByCompanyId((prev) => {
        const cur = prev[companyId];
        if (!cur) {
          return {
            ...prev,
            [companyId]: {
              isFollowing: false,
              followersCount: Math.max(0, followersCount),
              hydrated: true,
            },
          };
        }

        return {
          ...prev,
          [companyId]: {
            ...cur,
            followersCount: Math.max(0, followersCount),
            hydrated: true,
          },
        };
      });
    },
    [],
  );

  const value = useMemo(
    () => ({
      getState,
      seedCompany,
      applyFollowChange,
      adjustFollowersCount,
      setFollowersCount,
    }),
    [
      getState,
      seedCompany,
      applyFollowChange,
      adjustFollowersCount,
      setFollowersCount,
    ],
  );

  return (
    <CompanyFollowContext.Provider value={value}>
      {children}
    </CompanyFollowContext.Provider>
  );
};

export function useCompanyFollow(): CompanyFollowContextValue {
  const ctx = useContext(CompanyFollowContext);
  if (!ctx) {
    throw new Error(
      "useCompanyFollow must be used within a CompanyFollowProvider",
    );
  }
  return ctx;
}

/** Resolve display values: context first, then prop fallbacks. */
export function useCompanyFollowDisplay(
  companyId: string | undefined,
  fallback: SeedInput,
): SeedInput {
  const { getState, seedCompany } = useCompanyFollow();

  React.useEffect(() => {
    if (!companyId) return;
    seedCompany(companyId, fallback);
    // Only re-seed when identity or incoming server values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    fallback.isFollowing,
    fallback.followersCount,
    seedCompany,
  ]);

  const state = companyId ? getState(companyId) : undefined;
  return {
    isFollowing: state?.isFollowing ?? fallback.isFollowing,
    followersCount: state?.followersCount ?? fallback.followersCount,
  };
}
